import React, { Fragment, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'found';
import makeStyles from '@mui/styles/makeStyles';
import { CloseOutlined } from '@mui/icons-material';
import EventCard from 'ripple/Events/Components/EventCard';
import useIsMobile from 'lib/useIsMobile';
import { isPast } from 'date-fns';

const useStyles = makeStyles(() => ({
  activePreview: {
    zIndex: 1100,
    boxShadow: 'rgba(0, 0, 0, 0.75) 0px 3px 10px',
  },
  closeButton: {
    zIndex: 3,
    position: 'absolute',
    fontSize: 15,
    right: 5,
    top: 5,
    borderRadius: '50%',
  },
}));

const EventMinCard = ({
  focussedEvent,
  focussedRects,
  leftHandler,
  viewer,
  pageSize,
  handleLike,
  likeDetails,
  eventData,
  setEventData,
}) => {
  const classes = useStyles();
  const mobile = useIsMobile();
  const { router } = useRouter();
  const [sharing, setSharing] = useState(false);

  const expandListener = useCallback(
    event => {
      const { slug, acceptingApplicants, applicationEnds } = event || focussedEvent;

      if (focussedEvent.id) {
        if (
          !focussedEvent.application
          && [
            'rmpschardonnaychat',
            'svedkahalloween',
            'rmpsspiritsbarrelsaged',
          ].indexOf(slug) > -1
          && acceptingApplicants
          && !isPast(applicationEnds)
        ) {
          router.push(`/events/${slug}/view`);
        } else {
          router.push(`/events/${slug}`);
        }
      }
      leftHandler();
    },
    [focussedEvent, leftHandler, router],
  );

  return useMemo(() => {
    if (focussedRects) {
      const margins = index => {
        let val = 0.2;
        if (index === 0) {
          val = 0;
        } else if (pageSize - 1 === index) {
          val = 0.4;
        }
        return val;
      };

      const pos = focussedRects;
      const top = pos.y + window.scrollY - pos.height * 0.5;
      const left = pos.x - pos.width * margins(Math.floor(pos.x / pos.width));

      return (
        pos && (
          <div
            tabIndex={-1}
            style={
              focussedEvent
                ? {
                    position: 'absolute',
                    visibility: 'visible',
                    transition: 'width 0.03s height 0.03s',
                    top: `${top}px`,
                    left: `${left}px`,
                    overflow: 'hidden',
                    width: pos.width * (mobile && pageSize === 1 ? 1 : 1.4),
                  }
                : {
                    position: 'absolute',
                    visibility: 'hidden',
                    top: `${pos.y}px`,
                    left: `${pos.x}px`,
                    overflow: 'hidden',
                    width: pos.width,
                    height: pos.height,
                  }
            }
            className={focussedEvent ? classes.activePreview : ''}
          >
            {focussedEvent && (
              <Fragment>
                {mobile && (
                  <CloseOutlined
                    className={classes.closeButton}
                    onClick={leftHandler}
                  />
                )}
                <EventCard
                  setEventData={setEventData}
                  eventData={eventData}
                  likeDetails={likeDetails}
                  handleLike={handleLike}
                  onMouseLeave={() => {
                    if (!mobile && !sharing) {
                      setSharing(false);
                      leftHandler();
                    }
                  }}
                  setSharing={setSharing}
                  event={focussedEvent}
                  compact
                  key={focussedEvent.id}
                  viewer={viewer}
                  carouselItemClickListener={item => {
                    if (item.type !== 'a') {
                      expandListener(focussedEvent);
                    }
                    leftHandler();
                  }}
                  expandHandler={() => {
                    expandListener(focussedEvent);
                    leftHandler();
                  }}
                />
              </Fragment>
            )}
          </div>
        )
      );
    }
    return '';
  }, [
    focussedRects,
    focussedEvent,
    mobile,
    pageSize,
    classes,
    leftHandler,
    viewer,
    sharing,
    expandListener,
    likeDetails,
    eventData,
    setEventData,
  ]);
};

EventMinCard.propTypes = {
  focussedRects: PropTypes.object,
  viewer: PropTypes.object.isRequired,
  focussedEvent: PropTypes.object.isRequired,
  leftHandler: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
};

export default EventMinCard;
